import {
  Distribute,
  Icon,
  Spacer,
  SvgDomain,
  SvgUser,
  Text,
} from '@loomhq/lens';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import { renameCard } from 'actions/videosDashboard';
import {
  FOLDER_ID_FAVORITES,
  PAGETYPE_NAME_PERSONAL,
  PAGETYPE_NAME_SHARED,
  PAGETYPE_NAME_STARRED,
  PAGETYPE_NAME_TEAM,
  SPECIAL_FOLDER_ID_MY_VIDEOS,
  SPECIAL_FOLDER_ID_SHARED_VIDEOS,
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
  TEAM_VIDEOS_FOLDER_NAME,
} from 'constants/folders';
import {
  commitRenameFolderCreator,
  favoriteCurrentFolderCreator,
} from 'creators/videosDashboard';
import * as imageUtils from 'utilities/image';

import BreadcrumbsDropdown from 'components/videos-dashboard-ta/breadcrumb/dropdown';
import CardRenameInput from 'components/videos-dashboard-ta/card-rename-input';

import './styles.less';

const FOLDER_PRELOAD_ICONS = [
  require('img/icons/nested-folder-1-digit.svg'),
  require('img/icons/nested-folder-2-digit.svg'),
  require('img/icons/right-arrow-grey.svg'),
];

const getRootFolderId = rootFolderName => {
  switch (rootFolderName) {
    case 'My Videos':
    case PAGETYPE_NAME_PERSONAL:
      return SPECIAL_FOLDER_ID_MY_VIDEOS;

    case PAGETYPE_NAME_TEAM:
    case 'Team Videos':
    case TEAM_VIDEOS_FOLDER_NAME:
      return SPECIAL_FOLDER_ID_TEAM_VIDEOS;

    case PAGETYPE_NAME_SHARED:
      return SPECIAL_FOLDER_ID_SHARED_VIDEOS;

    case PAGETYPE_NAME_STARRED:
      return FOLDER_ID_FAVORITES;

    default:
      rootFolderName?.replace(' ', '-').toLowerCase();
  }
};

const getFirstCrumbName = name => {
  switch (name) {
    case 'My Videos':
      return PAGETYPE_NAME_PERSONAL;

    case 'Team Videos':
    case TEAM_VIDEOS_FOLDER_NAME:
      return PAGETYPE_NAME_TEAM;

    default:
      name;
  }
};

const getFirstCrumbIcon = name => {
  switch (name) {
    case 'My Videos':
      return <SvgUser />;

    case 'Team Videos':
    case TEAM_VIDEOS_FOLDER_NAME:
      return <SvgDomain />;

    default:
      return undefined;
  }
};

const CompactBreadcrumbComponent = class extends Component {
  render() {
    const { breadcrumbs, rootFolderName } = this.props;

    const rootFolderId = getRootFolderId(rootFolderName);
    const firstCrumbName = getFirstCrumbName(breadcrumbs[0]?.name);
    const firstCrumbIcon = getFirstCrumbIcon(breadcrumbs[0]?.name);

    return (
      <>
        {rootFolderId && (
          <a href={`/${rootFolderId}`}>
            <Distribute gap="small" alignment="center">
              {firstCrumbIcon && <Icon icon={firstCrumbIcon} />}
              {firstCrumbName && <Text>{firstCrumbName}</Text>}
            </Distribute>
          </a>
        )}
      </>
    );
  }
};

const Breadcrumb = class extends Component {
  render() {
    const { breadcrumbs, isFetchingFolders, rootFolderName } = this.props;

    // waiting until fetching folders is complete so we know what
    // workspace the user is in and can display the correct page header
    if (isFetchingFolders) {
      return null;
    }

    const rootFolderId = getRootFolderId(rootFolderName);
    const firstCrumbName = getFirstCrumbName(breadcrumbs[0]?.name);

    let breadcrumbsToShow = null;
    let dropdownBreadCrumbs = null;
    let nestedFolderButton = null;

    if (breadcrumbs.length > 2) {
      dropdownBreadCrumbs = breadcrumbs.slice(0, -1).reverse();

      const lastBreadCrumb = (
        <div className="crumb-wrapper">
          {this.lastBreadcrumb([].concat(breadcrumbs).pop())}
        </div>
      );

      breadcrumbsToShow = lastBreadCrumb;

      nestedFolderButton = (
        <Fragment>
          <BreadcrumbsDropdown
            dropdownBreadCrumbs={dropdownBreadCrumbs}
            onClicked={this.props.onBreadcrumbClicked}
          />

          <div className="breadcrumbArrowSection">/</div>
        </Fragment>
      );
    } else if (breadcrumbs.length === 2) {
      const [firstCrumb, secondCrumb] = breadcrumbs;

      breadcrumbsToShow = (
        <div className="crumb-wrapper">
          <a href={`/${rootFolderId}`}>
            <Spacer right="xsmall">
              <Text>{firstCrumb.name}</Text>
            </Spacer>
          </a>
          <div className="breadcrumbArrowSection">/</div>
          {this.lastBreadcrumb(secondCrumb)}
        </div>
      );
    } else if (breadcrumbs.length === 1) {
      breadcrumbsToShow = (
        <a href={`/${rootFolderId}`}>
          <Text fontWeight="bold">{firstCrumbName}</Text>
        </a>
      );
    }

    return (
      <div className="breadcrumb">
        {nestedFolderButton}
        {breadcrumbsToShow}
      </div>
    );
  }

  lastBreadcrumb = breadcrumb => {
    const { renameId, renameCard } = this.props;

    if (breadcrumb.id === renameId) {
      return (
        <CardRenameInput
          name={breadcrumb.name}
          onSubmit={this.commitRenameFolder}
        />
      );
    }

    return (
      <div
        onClick={() => renameCard(breadcrumb.id)}
        className="last-breadcrumb"
      >
        <Text fontWeight="bold" hasEllipsis>
          {breadcrumb.name}
        </Text>
      </div>
    );
  };

  componentDidMount() {
    this.preloadFolderIcons();
  }

  handleTopLevelClick = event => {
    event.preventDefault();

    this.props.onBreadcrumbClicked(1);
  };

  favoriteFolder(e, id, favorite) {
    e.stopPropagation();

    this.props.favoriteCurrentFolder(id, !favorite);
  }

  preloadFolderIcons() {
    FOLDER_PRELOAD_ICONS.forEach(imageUtils.createImage);
  }

  commitRenameFolder = newName => {
    const { renameId } = this.props;

    this.props.commitRenameFolder(renameId, newName);
  };
};

const mapStateToProps = state => {
  const {
    folder_id: folderId,
    is_archived: isArchived,
    is_favorite: isFavorite,
    read_only: readOnly,
    is_fetching_folders: isFetchingFolders,
    rename_id: renameId,
    breadcrumbs: dashboardBreadcrumbs,
  } = state.videos_dashboard;

  const { video_folders: videoBreadcrumbs } = state.share_video;

  const breadcrumbs =
    dashboardBreadcrumbs.length > 0 ? dashboardBreadcrumbs : videoBreadcrumbs;
  const rootFolderName =
    videoBreadcrumbs.length > 0
      ? videoBreadcrumbs[0]?.name
      : dashboardBreadcrumbs[0]?.name;

  return {
    breadcrumbs,
    folderId,
    isArchived,
    isFavorite,
    readOnly,
    isFetchingFolders,
    renameId,
    rootFolderName,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    favoriteCurrentFolder: (id, favorite) =>
      dispatch(favoriteCurrentFolderCreator(id, favorite)),
    onBreadcrumbClicked: levels => {
      const levelsUp = new Array(levels).fill('..');

      // If there's no trailing slash, it's considered a file, not a folder, so go to current directory instead
      if (!window.location.pathname.endsWith('/')) {
        levelsUp[0] = '.';
      }

      window.location.href = levelsUp.join('/');
    },
    commitRenameFolder: (id, name) => {
      dispatch(commitRenameFolderCreator(id, name));
    },
    renameCard: id => dispatch(renameCard(id)),
  };
};

const CompactBreadcrumb = connect(
  mapStateToProps,
  mapDispatchToProps
)(CompactBreadcrumbComponent);

export { CompactBreadcrumb };
export default connect(mapStateToProps, mapDispatchToProps)(Breadcrumb);
